import {Injectable} from '@angular/core';
import {DateTime} from 'luxon';
import {TtWorkingTimeEntry} from '../../interfaces/time-tracking/tt-working-time-entry';
import {TtActivityEntry} from '../../interfaces/time-tracking/tt-activity-entry';
import {TtWorkingTimeEntriesTable} from '../../tables/time-tracking/tt-working-time-entries.table';
import {TtActivityEntriesTable} from '../../tables/time-tracking/tt-activity-entries.table';
import {TimeTrackingHelper} from '../../helpers/time-tracking-helper';
import {TtWorkingTimeEntryFactory} from '../../factories/time-tracking/tt-working-time-entry-factory';
import {HashHelper} from '../../helpers/hash-helper';
import {TtWorkingTimeTypesTable} from '../../tables/time-tracking/tt-working-time-types.table';
import {db} from '../../tables/db.service';
import {TtWorkingTimeEntriesResource} from '../../resources/tt-working-time-entries.resource';
import {ErrorMessagesService} from '../core/error-messages.service';
import {firstValueFrom} from 'rxjs';
import {ErrorMessageType} from '../../enums/core/error-message-type';
import {TimeHelper} from '../../helpers/time-helper';
import {WorkingTimeCalculations} from '../../interfaces/time-tracking/working-time-calculations';
import {WorkingTimeCalculationsFactory} from '../../factories/time-tracking/working-time-calculations-factory';
import {EmployeeRostersTable} from '../../tables/employees-roster/employee-rosters.table';
import {EmployeeRoster} from '../../interfaces/employee-rosters/employee-roster';
import {TtActivityTypesTable} from '../../tables/time-tracking/tt-activity-types.table';
import {TtWorkingTimeType} from '../../interfaces/time-tracking/tt-working-time-type';
import {TtActivityType} from '../../interfaces/time-tracking/tt-activity-type';
import {AuthService} from '../core/auth.service';
import {UserService} from '../core/user.service';

@Injectable({
  providedIn: 'root'
})
export class TtWorkingTimeEntriesServices {
  private calculateQueue: Promise<void> = Promise.resolve();
  private calculateRequestId: number = 0;

  constructor(private ttWorkingTimeEntriesTable: TtWorkingTimeEntriesTable,
              private ttWorkingTimeEntriesResource: TtWorkingTimeEntriesResource,
              private employeeRostersTable: EmployeeRostersTable,
              private ttWorkingTimeTypesTable: TtWorkingTimeTypesTable,
              private errorMessagesService: ErrorMessagesService,
              private ttActivityTypesTable: TtActivityTypesTable,
              private ttActivityEntriesTable: TtActivityEntriesTable) {
  }

  /**
   *
   * @param query
   */
  public async loadTtWorkingTimeEntries(query: {
    dateFrom?: string,
    dateTo?: string,
    employeeId?: number
  } = {}, clearAll: boolean = false): Promise<TtWorkingTimeEntry[]> {
    try {
      const ttWorkingTimeEntries = await firstValueFrom(this.ttWorkingTimeEntriesResource.load(query));
      if(clearAll) {
        await this.ttWorkingTimeEntriesTable.clear();
      }
      else {
        await this.ttWorkingTimeEntriesTable.removeByPeriod(DateTime.fromISO(query.dateFrom), DateTime.fromISO(query.dateTo));
      }
      await this.ttWorkingTimeEntriesTable.addMany(ttWorkingTimeEntries);
      // Calculate only the current day
      await this.calculateTtWorkingTimeEntries();
      return ttWorkingTimeEntries;
    }
    catch (e) {
      await this.errorMessagesService.showErrorMessage(ErrorMessageType.Connection);
      console.error(e);
      throw e;
    }
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async calculateWorkingTime(dateFrom: string | DateTime | Date, dateTo: string | DateTime | Date): Promise<number> {
    const today = DateTime.local().toISODate();
    dateFrom = TimeHelper.getDateString(dateFrom);
    dateTo = TimeHelper.getDateString(dateTo);

    if(dateFrom > today) {
      return 0;
    }
    if(dateTo > today) {
      dateTo = today;
    }

    const ttWorkingTimeEntries = await this.ttWorkingTimeEntriesTable.getByPeriod(dateFrom, dateTo);

    return ttWorkingTimeEntries
      .filter(_ttWorkingTimeEntry => _ttWorkingTimeEntry.ttWorkingTimeType.isInCalculation)
      .reduce((_duration, _ttWorkingTimeEntry) => {
        return _duration + _ttWorkingTimeEntry.duration;
      }, 0);
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async calculateTtWorkingTimeEntries(
    dateFrom: string | DateTime | Date = new Date(),
    dateTo: string | DateTime | Date = new Date()
  ): Promise<TtWorkingTimeEntry[]> {
    const requestId = ++this.calculateRequestId;
    let calculatedEntries: TtWorkingTimeEntry[] = [];

    this.calculateQueue = this.calculateQueue
      .catch(() => null)
      .then(async() => {
        dateFrom = TimeHelper.getDateTime(dateFrom);
        dateTo = TimeHelper.getDateTime(dateTo);

        const ttActivityTypeTypes = await this.ttActivityTypesTable.getAll();
        const ttActivityTypeTypeForTravelTime =
          ttActivityTypeTypes.find(_ttActivityType => {
            return _ttActivityType.isTravelTime && _ttActivityType.ttWorkingTimeTypes.length > 0;
          });

        const ttWorkingTimeTypeForTravelTime: TtWorkingTimeType = ttActivityTypeTypeForTravelTime?.ttWorkingTimeTypes[0];

        let ttActivityEntries: TtActivityEntry[];

        if (dateFrom) {
          ttActivityEntries = await this.ttActivityEntriesTable.getByPeriod(dateFrom as DateTime, dateTo as DateTime);
        }
        else {
          ttActivityEntries = await this.ttActivityEntriesTable.getAll();
        }

        const ttWorkingTimeEntries: TtWorkingTimeEntry[] = [];
        let currentTtWorkingTimeEntry: TtWorkingTimeEntry = null;

        for (const ttActivityEntry of ttActivityEntries) {
          this.validateTtActivityEntry(ttActivityEntry);

          let splitTravelTime = false;
          let handledTravelBeforeNonWorking = false; // CHANGED

          if (ttActivityEntry.ttActivityType.isStandby) {
            const ttWorkingTimeEntry = await this.createFromTtActivityEntry(ttActivityEntry);
            ttWorkingTimeEntries.push(ttWorkingTimeEntry);
            continue;
          }

          const _timeFrom = DateTime.fromISO(ttActivityEntry.timeFrom);
          let timeFromTravel = ttActivityEntry.timeFromTravel
            ? DateTime.fromISO(ttActivityEntry.timeFromTravel)
            : _timeFrom;
          let timeFromTravelIso = timeFromTravel.toISO();
          const hasTravelTime = !_timeFrom.hasSame(timeFromTravel, 'minute');

          // CHANGED: Immer Reisezeit vor Nicht-Arbeitszeit erzeugen/verlängern
          if (!ttActivityEntry.ttActivityType.isWorkingTime && hasTravelTime && ttActivityTypeTypeForTravelTime) {
            if (
              currentTtWorkingTimeEntry &&
              currentTtWorkingTimeEntry.ttWorkingTimeType?.id === ttWorkingTimeTypeForTravelTime?.id &&
              DateTime.fromISO(currentTtWorkingTimeEntry.timeTo).hasSame(timeFromTravel, 'minute')
            ) {
              // vorangegangene Reisezeit verlängern
              currentTtWorkingTimeEntry.timeTo = ttActivityEntry.timeFrom;
              currentTtWorkingTimeEntry.duration = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo)
                .diff(DateTime.fromISO(currentTtWorkingTimeEntry.timeFrom), 'minutes').as('minutes');
            } else {
              // neuen Reisezeit-Eintrag anlegen
              const travelEntry = this.createFromTtActivityEntryType(
                ttActivityTypeTypeForTravelTime,
                timeFromTravelIso,
                ttActivityEntry.timeFrom
              );
              ttWorkingTimeEntries.push(travelEntry);
            }
            // Danach soll die Nicht-Arbeitszeit normal ab timeFrom starten
            timeFromTravel = _timeFrom;
            timeFromTravelIso = ttActivityEntry.timeFrom;
            handledTravelBeforeNonWorking = true;
          }
          // --- Ende CHANGED ---

          if (currentTtWorkingTimeEntry) {
            // CHANGED: Vergleichs-Startzeit
            const timeFrom = ttActivityEntry.ttActivityType.isWorkingTime
              ? (timeFromTravelIso
                ? DateTime.fromISO(timeFromTravelIso)
                : DateTime.fromISO(ttActivityEntry.timeFrom))
              : DateTime.fromISO(ttActivityEntry.timeFrom);
            // Ende CHANGED

            const timeTo = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo);

            if (timeTo.hasSame(timeFrom, 'minute')) {
              if (
                currentTtWorkingTimeEntry.ttWorkingTimeTypeId === ttActivityEntry.ttWorkingTimeTypeId &&
                (ttActivityEntry.ttActivityType.isWorkingTime || !hasTravelTime)
              ) {
                currentTtWorkingTimeEntry.timeTo = ttActivityEntry.timeTo;
                currentTtWorkingTimeEntry.duration = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo)
                  .diff(DateTime.fromISO(currentTtWorkingTimeEntry.timeFrom), 'minutes').as('minutes');
                continue;
              }
              else {
                if (!ttActivityEntry.ttActivityType.isWorkingTime && hasTravelTime) {
                  if (currentTtWorkingTimeEntry.ttWorkingTimeType.id === ttWorkingTimeTypeForTravelTime?.id) {
                    currentTtWorkingTimeEntry.timeTo = ttActivityEntry.timeFrom;
                    currentTtWorkingTimeEntry.duration = DateTime.fromISO(currentTtWorkingTimeEntry.timeTo)
                      .diff(DateTime.fromISO(currentTtWorkingTimeEntry.timeFrom), 'minutes').as('minutes');
                    timeFromTravel = _timeFrom;
                    timeFromTravelIso = ttActivityEntry.timeFrom;
                  }
                  else {
                    splitTravelTime = true;
                  }
                }
              }
            }
          }
          else {
            // CHANGED: Doppelanlage verhindern, wenn oben bereits behandelt
            if (!handledTravelBeforeNonWorking && !ttActivityEntry.ttActivityType.isWorkingTime && hasTravelTime) {
              splitTravelTime = true;
            }
            // Ende CHANGED
          }

          if (splitTravelTime && ttActivityTypeTypeForTravelTime) {
            const newTtWorkingTimeEntry = this.createFromTtActivityEntryType(
              ttActivityTypeTypeForTravelTime,
              timeFromTravelIso,
              ttActivityEntry.timeFrom
            );
            ttWorkingTimeEntries.push(newTtWorkingTimeEntry);
            timeFromTravel = _timeFrom;
            timeFromTravelIso = ttActivityEntry.timeFrom;
          }

          currentTtWorkingTimeEntry = await this.createFromTtActivityEntry(
            ttActivityEntry,
            ttActivityEntry.ttActivityType.isWorkingTime ? timeFromTravel : _timeFrom
          );
          ttWorkingTimeEntries.push(currentTtWorkingTimeEntry);
        }

        if (requestId !== this.calculateRequestId) {
          return;
        }

        if (dateFrom) {
          await this.ttWorkingTimeEntriesTable.removeByDate(dateFrom);
        }
        else {
          await this.ttWorkingTimeEntriesTable.clear();
        }
        await this.ttWorkingTimeEntriesTable.addMany(ttWorkingTimeEntries);
        calculatedEntries = ttWorkingTimeEntries;
      })
      .catch(async(e) => {
        console.error(e);
        await this.errorMessagesService.showErrorMessage(ErrorMessageType.Storage);
      });

    await this.calculateQueue;

    if (calculatedEntries.length > 0) {
      return calculatedEntries;
    }
    return this.ttWorkingTimeEntriesTable.getByPeriod(dateFrom, dateTo);
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public async getWorkingTimeCalculations(dateFrom: string | DateTime | Date, dateTo: string | DateTime | Date): Promise<WorkingTimeCalculations> {
    dateFrom = TimeHelper.getDateString(dateFrom);
    dateTo = TimeHelper.getDateString(dateTo);

    const workingTimeCalculations = WorkingTimeCalculationsFactory.new();

    const ttWorkingTimeEntries: TtWorkingTimeEntry[] = await this.ttWorkingTimeEntriesTable.getByPeriod(dateFrom, dateTo);
    const employeeRosters: EmployeeRoster[] = await this.employeeRostersTable.getByPeriod(dateFrom, dateTo);

    const today = DateTime.local().toISODate();

    for(const ttWorkingTimeEntry of ttWorkingTimeEntries) {
      if(ttWorkingTimeEntry.ttWorkingTimeType.isInCalculation) {
        const date = TimeHelper.getDateString(ttWorkingTimeEntry.timeFrom);
        if(date < today) {
          workingTimeCalculations.workingTime += ttWorkingTimeEntry.duration;
        }
        workingTimeCalculations.totalWorkingTime += ttWorkingTimeEntry.duration;
      }
    }

    for(const employeeRoster of employeeRosters) {
      const date = TimeHelper.getDateString(employeeRoster.date);
      workingTimeCalculations.plannedWorkingTime += employeeRoster.workingMinutes;

      if(date >= today) {
        workingTimeCalculations.workingTimeExtrapolated += employeeRoster.workingMinutes;
      }
    }

    workingTimeCalculations.workingTimeExtrapolated += workingTimeCalculations.workingTime;

    workingTimeCalculations.plannedWorkingTimeDifference =
      workingTimeCalculations.workingTime - workingTimeCalculations.plannedWorkingTime;

    workingTimeCalculations.workingTimeExtrapolatedDifference =
      workingTimeCalculations.workingTimeExtrapolated - workingTimeCalculations.plannedWorkingTime;

    return workingTimeCalculations;
  }

  /**
   *
   * @param ttActivityEntry
   * @private
   */
  private async createFromTtActivityEntry(
    ttActivityEntry: TtActivityEntry,
    timeFromOverride: DateTime = null
  ): Promise<TtWorkingTimeEntry> {
    const ttWorkingTimeEntry = TtWorkingTimeEntryFactory.new();

    // ----------------------------- CHANGED: Startzeit beim Erstellen -----------------------------
    const timeFrom = timeFromOverride ?? (ttActivityEntry.ttActivityType.isWorkingTime
      ? TimeTrackingHelper.getTimeFrom(ttActivityEntry)          // darf travel berücksichtigen
      : DateTime.fromISO(ttActivityEntry.timeFrom));             // NIE travel für Nicht-Arbeitszeit
    // ---------------------------------------------------------------------------------------------

    const ttWorkingTimeTypeId = ttActivityEntry.ttWorkingTimeTypeId;
    const ttWorkingTimeType = await this.ttWorkingTimeTypesTable.get(ttWorkingTimeTypeId);

    if(!ttWorkingTimeType) {
      throw new Error('No ttWorkingTimeTypes found');
    }

    ttWorkingTimeEntry.id = HashHelper.getGuid();
    ttWorkingTimeEntry.timeFrom = timeFrom.toISO();
    ttWorkingTimeEntry.timeTo = ttActivityEntry.timeTo;
    ttWorkingTimeEntry.ttWorkingTimeType = ttWorkingTimeType;
    ttWorkingTimeEntry.ttWorkingTimeTypeId = ttWorkingTimeType.id;
    ttWorkingTimeEntry.employeeId = ttActivityEntry.employeeId;
    ttWorkingTimeEntry.duration = DateTime.fromISO(ttActivityEntry.timeTo)
      .diff(timeFrom, 'minutes').as('minutes');
    ttWorkingTimeEntry.employee = ttActivityEntry.employee;
    return ttWorkingTimeEntry;
  }

  private validateTtActivityEntry(ttActivityEntry: TtActivityEntry): void {
    if(!ttActivityEntry?.ttActivityType) {
      throw new Error(`No ttActivityType found for ttActivityEntry ${ttActivityEntry?.uuid ?? ttActivityEntry?.id ?? 'unknown'}`);
    }

    const timeFrom = DateTime.fromISO(ttActivityEntry.timeFrom);
    if(!timeFrom.isValid) {
      throw new Error(`Invalid timeFrom for ttActivityEntry ${ttActivityEntry?.uuid ?? ttActivityEntry?.id ?? 'unknown'}`);
    }

    const timeTo = DateTime.fromISO(ttActivityEntry.timeTo);
    if(!timeTo.isValid) {
      throw new Error(`Invalid timeTo for ttActivityEntry ${ttActivityEntry?.uuid ?? ttActivityEntry?.id ?? 'unknown'}`);
    }

    if(ttActivityEntry.timeFromTravel) {
      const timeFromTravel = DateTime.fromISO(ttActivityEntry.timeFromTravel);
      if(!timeFromTravel.isValid) {
        throw new Error(`Invalid timeFromTravel for ttActivityEntry ${ttActivityEntry?.uuid ?? ttActivityEntry?.id ?? 'unknown'}`);
      }
    }
  }

  /**
   *
   * @param ttActivityType
   * @param timeFrom
   * @param timeTo
   * @private
   */
  private createFromTtActivityEntryType(ttActivityType: TtActivityType, timeFrom: string, timeTo: string): TtWorkingTimeEntry {
    const ttWorkingTimeEntry = TtWorkingTimeEntryFactory.new();
    const ttWorkingTimeType = ttActivityType.ttWorkingTimeTypes[0];

    ttWorkingTimeEntry.id = HashHelper.getGuid();
    ttWorkingTimeEntry.timeFrom = timeFrom;
    ttWorkingTimeEntry.timeTo = timeTo;
    ttWorkingTimeEntry.ttWorkingTimeType = ttWorkingTimeType;
    ttWorkingTimeEntry.ttWorkingTimeTypeId = ttWorkingTimeType.id;
    ttWorkingTimeEntry.employeeId = UserService.getUser().employee.id;
    ttWorkingTimeEntry.duration = DateTime.fromISO(timeTo)
      .diff(DateTime.fromISO(timeFrom), 'minutes').as('minutes');
    ttWorkingTimeEntry.employee = UserService.getUser().employee;

    return ttWorkingTimeEntry;
  }

}
